import React from 'react'
import PropTypes from 'prop-types'
import defaultTheme from 'components/_base/theme'
import { generateMediaQueries } from 'components/_base/breakpoints'
import { ThemeContext, ThemeProvider } from 'styled-components'
import { useLocation } from 'react-router-dom'

const maxWindowWidth = 5000

const sizes = {
  xs: maxWindowWidth,
  sm: maxWindowWidth,
  md: maxWindowWidth,
  td: maxWindowWidth,
  lg: maxWindowWidth,
  xl: maxWindowWidth,
}

const devices = generateMediaQueries(sizes)

const OverwriteThemeProvider = ({ theme = {}, children }) => (
  <ThemeProvider theme={{ ...defaultTheme, ...theme }}>
    {children}
  </ThemeProvider>
)

const MobileSwitchContext = React.createContext(undefined)

const MobileSwitchProvider = ({
  forceMobile = false,
  breakpoint,
  children,
}) => {
  const [isMobile, setIsMobile] = React.useState(false)
  const theme = React.useContext(ThemeContext)
  const location = useLocation().pathname
  const [currentView, setCurrentView] = React.useState('')

  // set current view
  React.useEffect(() => {
    let view = ''

    if (location.includes('merkliste')) {
      view = 'merkliste'
    } else if (location.includes('vergleich')) {
      view = 'vergleich'
    } else if (location.includes('krankenhaus') || location.includes('arzt')) {
      view = 'detail'
    } else {
      view = 'suche'
    }
    setCurrentView(view)
  }, [location])

  // compare current res with breakpoint
  React.useEffect(() => {
    const onResize = () => {
      const size = theme.breakpoints.sizes[breakpoint]

      switch (currentView) {
        case 'merkliste':
        case 'vergleich':
        case 'detail':
          setIsMobile(size > window.innerWidth)
          break

        default:
          setIsMobile(theme.breakpoints.sizes.td > window.innerWidth)
      }
    }
    // init isMobile
    onResize()
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [theme, breakpoint, currentView])

  return (
    <MobileSwitchContext.Provider value={ isMobile }>
      {forceMobile ? (
        <OverwriteThemeProvider
          theme={{
            breakpoints: {
              sizes,
              devices,
            },
            forceMobile: {
              forceMobile,
            },
          }}>
          {children}
        </OverwriteThemeProvider>
      ) : (
        children
      )}
    </MobileSwitchContext.Provider>
  )
}

MobileSwitchProvider.propTypes = {
  /** breakpoint that switch isMobile */
  breakpoint: PropTypes.oneOf(['xs', 'sm', 'md', 'td', 'lg', 'xl']),
  /**
   * create a new theme provider that overwrites
   * media queries to force mobile behavior on
   * desktop
   */
  forceMobile: PropTypes.bool,
}

const useMobileSwitch = () => {
  const context = React.useContext(MobileSwitchContext)
  if (context === undefined) {
    throw new Error('useMobilSwitch must be used within a MobileSwitchProvider')
  }
  return context
}

const MobileSwitchConsumer = ({ children }) => (
  <MobileSwitchContext.Consumer>
    {(context) => {
      if (context === undefined) {
        throw new Error(
          'MobileSwitchConsumer must be used within a MobileSwitchProvider'
        )
      }
      return children(context)
    }}
  </MobileSwitchContext.Consumer>
)

export { MobileSwitchProvider, MobileSwitchConsumer, useMobileSwitch }
